import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export default function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ code: "", discount_type: "percent", value: 10, min_order: 0, active: true });

    const fetchAll = () => {
        setLoading(true);
        api.get("/coupons")
            .then(({ data }) => setCoupons(data.coupons))
            .finally(() => setLoading(false));
    };

    useEffect(fetchAll, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/coupons", {
                ...form,
                value: parseFloat(form.value),
                min_order: parseFloat(form.min_order),
            });
            toast.success("Coupon created");
            setForm({ code: "", discount_type: "percent", value: 10, min_order: 0, active: true });
            fetchAll();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this coupon?")) return;
        await api.delete(`/coupons/${id}`);
        fetchAll();
    };

    return (
        <div data-testid="admin-coupons">
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Atelier</p>
            <h1 className="mt-2 font-display text-4xl text-white">Coupons</h1>

            <form onSubmit={create} className="mt-8 grid grid-cols-1 gap-4 border border-border-dim bg-surface p-6 md:grid-cols-6">
                <input
                    required
                    placeholder="Code (e.g. LOVE20)"
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    data-testid="coupon-code"
                    className="md:col-span-2 border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold"
                />
                <select
                    value={form.discount_type}
                    onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
                    className="border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold"
                >
                    <option value="percent">Percent %</option>
                    <option value="fixed">Fixed $</option>
                </select>
                <input
                    required
                    type="number"
                    step="0.01"
                    placeholder="Value"
                    value={form.value}
                    onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                    data-testid="coupon-value"
                    className="border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold"
                />
                <input
                    type="number"
                    step="0.01"
                    placeholder="Min order"
                    value={form.min_order}
                    onChange={(e) => setForm((f) => ({ ...f, min_order: e.target.value }))}
                    className="border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold"
                />
                <button
                    type="submit"
                    data-testid="coupon-create"
                    className="inline-flex items-center justify-center gap-2 bg-gold px-4 py-2 text-[11px] uppercase tracking-widest text-obsidian hover:bg-gold-light"
                >
                    <Plus className="h-3 w-3" /> Create
                </button>
            </form>

            <div className="mt-8 border border-border-dim bg-surface">
                <div className="hidden grid-cols-12 gap-4 border-b border-border-dim px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-white/40 lg:grid">
                    <div className="col-span-3">Code</div>
                    <div className="col-span-3">Discount</div>
                    <div className="col-span-2">Min order</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-6 text-white/40">Loading…</div>
                ) : coupons.length === 0 ? (
                    <div className="p-6 text-white/40">No coupons yet.</div>
                ) : (
                    coupons.map((c) => (
                        <div key={c.id} className="border-b border-border-dim p-4 last:border-b-0 lg:grid lg:grid-cols-12 lg:items-center lg:gap-4 lg:px-6">
                            <div className="flex items-center justify-between lg:col-span-3 lg:block">
                                <span className="font-display text-xl text-gold sm:text-2xl lg:text-lg">{c.code}</span>
                                <button
                                    onClick={() => remove(c.id)}
                                    data-testid={`coupon-delete-${c.id}`}
                                    className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/60 hover:border-heart hover:text-heart lg:hidden"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs lg:contents lg:mt-0">
                                <span className="text-white lg:col-span-3 lg:text-sm">
                                    {c.discount_type === "percent" ? `${c.value}%` : `$${c.value}`}
                                </span>
                                <span className="text-white/70 lg:col-span-2 lg:text-sm">Min ${c.min_order || 0}</span>
                                <span className="uppercase tracking-widest text-white/60 lg:col-span-2">
                                    {c.active ? "Active" : "Disabled"}
                                </span>
                            </div>
                            <div className="hidden justify-end lg:col-span-2 lg:flex">
                                <button
                                    onClick={() => remove(c.id)}
                                    className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/60 hover:border-heart hover:text-heart"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
